import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import LoadingSpinner from "../../LoadingSpinner";
import { PaymentTypes, SORT } from "../../../../util";
import { getAgentFinancialTransactions } from "../../../../actions";
import { useInfiniteAsyncScroll } from "../../../../hooks/useInfiniteAsyncScroll";
import { newColumn, newColumnByNestedProps, newColumnDateByLocale, newColumnText, newColumnValueWithNumberComponent, TableList } from "../../TableList";

const useStyles = makeStyles((theme) => ({
  link: {
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
  linkSecondary: {
    textDecoration: "none",
    color: theme.palette.text.secondary,
  },
  rowsResult: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: "block",
    alignItems: "center",
  },
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    gap: theme.spacing(2),
  },
  leftText: {
    marginRight: theme.spacing(2),
    textAlign: "left",
  },
  rightText: {
    marginLeft: "auto",
    textAlign: "right",
  },
}));

const options = {
  exportButton: false,
  search: false,
  filtering: false,
  paging: false,
  toolbar: false,
  exportDelimiter: "\t",
  exportFileName: "table",
  padding: "dense",
  headerStyle: {
    fontSize: 12,
    color: "#FFBE2D",
    textAlignLast: "start",
    whiteSpace: "nowrap",
  },
};

const ORDER_BY = {
  paymentDate: "DATE",
  description: "DESCRIPTION",
  value: "VALUE",
  payed: "PAYED",
  "contract.id": "CONTRACT_ID",
  "contract.userId": "CLIENT_ID",
};

const PaymentsTable = ({ currentUser, agent, dateLabel, startDate, finalDate }) => {
  const classes = useStyles();
  const [totalRecordsPayments, setTotalRecordsPayments] = React.useState(0);
  const scrollTriggerRef = React.useRef(null);
  const [orderBy, setOrderBy] = React.useState(ORDER_BY.paymentDate);
  const [sort, setSort] = React.useState(SORT.asc);

  const columns = React.useMemo(
    () => [
      newColumnDateByLocale("app.date", "paymentDate"),
      newColumn("app.description", "description", "string", "left", (row) => {
        const isLeaderCommission = row?.type === PaymentTypes.BONUS_LEADERSHIP_MONTHLY;
        return (
          <Typography variant="caption">
            <FormattedMessage
              id={`app.commission.origin.type.${row.paymentType}`}
              values={{ id: row.contract.id }}
            />
            {isLeaderCommission && " - Leader"}
          </Typography>
        );
      }),
      newColumnValueWithNumberComponent("app.value", "value", currentUser.taxResidence, currentUser.hideValues),
      newColumnText("app.status", "payed", (e) => <FormattedMessage id={e.payed ? "app.paid" : "app.enuns.PENDING"} />),
      newColumnByNestedProps("app.lc", "contract.id", "string", "right", (rowData) => (
        <Typography
          variant="caption"
          component={Link}
          to={`/userContract/${rowData.contract.id}`}
          className={classes.link}
        >
          {`C ${rowData.contract.id}`}
        </Typography>
      )),
      newColumnByNestedProps("app.customer", "contract.userId", "string", "center", (rowData) => (
        <Typography
          variant="caption"
          className={classes.link}
          component={Link}
          to={`/user-detail/${rowData.contract.userId}`}
        >
          {`${rowData.contract.name} `} <span className={classes.linkSecondary}>{`(${rowData.contract.userId})`}</span>
        </Typography>
      )),
    ],
    [currentUser.taxResidence, currentUser.hideValues, classes.link, classes.linkSecondary],
  );

  const fetchPayments = async ({ params, skip = 0, take = 10 }) => {
    const submitParams = {
      startDate: params.startDate.format("YYYY-MM-DD"),
      endDate: params.finalDate.format("YYYY-MM-DD"),
      orderBy: params.orderBy,
      sortDirection: params.sort,
      skip,
      take,
    };

    const paymentsObj = await getAgentFinancialTransactions(params.agentId, submitParams);

    if (skip === 0) {
      setTotalRecordsPayments(paymentsObj.total);
    }

    return paymentsObj;
  };

  const memoParams = React.useMemo(
    () => ({
      agentId: agent.id,
      startDate,
      finalDate,
      orderBy,
      sort,
    }),
    [agent.id, startDate, finalDate, orderBy, sort],
  );

  const {
    error,
    items: payments,
    loading,
  } = useInfiniteAsyncScroll({
    idField: "paymentId",
    onScroll: fetchPayments,
    params: memoParams,
    take: 100,
    mapResponse: (res) => ({
      newItems: res?.items ?? [],
      total: res?.total ?? null,
    }),
  });

  if (error) {
    return (
      <Typography
        align="center"
        color="error"
        style={{ marginTop: 20 }}
      >
        <FormattedMessage
          id={error}
          defaultMessage={error}
        />
      </Typography>
    );
  }

  return (
    <>
      {payments?.length ? (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap-reverse"
            className={classes.container}
          >
            <Typography
              variant="body2"
              color="textPrimary"
              className={classes.leftText}
            >
              <FormattedMessage
                id="app.report.payments.periodList"
                values={{ period: dateLabel }}
              />
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.rightText}
            >
              {totalRecordsPayments === 1 ? (
                <FormattedMessage id="app.report.payments.oneRow" />
              ) : (
                <FormattedMessage
                  id="app.report.payments.multiRow"
                  values={{ rows: totalRecordsPayments }}
                />
              )}
            </Typography>
          </Box>

          <TableList
            data={payments}
            columns={columns}
            options={options}
            country={currentUser.taxResidence}
            user={currentUser}
            preserveTitle
            onOrderChange={(orderBy, orderDirection) => {
              const column = columns[orderBy];
              setOrderBy(ORDER_BY[column.field]);
              setSort(SORT[orderDirection]);
            }}
          />
        </>
      ) : (
        !loading && (
          <Typography
            align="center"
            style={{ marginTop: 20 }}
          >
            <FormattedMessage id="app.report.payments.empty" />
          </Typography>
        )
      )}

      {loading && (
        <LoadingSpinner
          marginTop={20}
          message={<FormattedMessage id="app.payments.searching" />}
        />
      )}

      <div ref={scrollTriggerRef} />
    </>
  );
};

export default PaymentsTable;
